import { Box, styled } from '@mui/material';
import { LocaleContext } from 'new/i18n/LocaleContext';
import { Plan } from 'new/modules/MembershipPlansManage/types';
import { formatNumberAsCurrency } from 'new/utils/numbers';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';

const Wrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',

  '.plan_box-wrapper': {
    padding: '0 0 1rem 0',
    display: 'flex',
  },

  '.plan_box': {
    backgroundColor: 'white',
    border: '1px solid #ccc',
    position: 'relative',
    padding: '0 0 3rem 0',
    margin: '0 0.5rem',
    width: '360px',

    li: {
      fontSize: '0.875rem',
    },
    '.plan_description': {
      padding: '15px',
      minHeight: '13.5rem',
    },

    '.plan_header': {
      fontFamily: 'Barlow Semi Condensed',
      fontWeight: 700,
      fontSize: '18px',
      paddingBottom: '0.5rem',
    },

    '.plan_prices': {
      backgroundColor: '#ccc',
      textAlign: 'center',
      height: '3rem',
      lineHeight: '3rem',
      position: 'absolute',
      bottom: 0,
      width: '100%',
    },

    '.age_limits': {
      fontSize: '.8rem',
      color: '#878787',
    },
  },
}));

const StyledBoxSaveAmount = styled(Box)({
  textAlign: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
});

type Props = {
  memberships: Plan[];
  smBusinessInfo: {
    smbizBusinessInvites: {
      discountPercentage: number;
    };
  };
};

const ClassicPlans: React.FC<Props> = ({ memberships, smBusinessInfo }) => {
  const intl = useIntl();
  const { locale } = useContext(LocaleContext);
  const isSpanish = locale === 'es-US';

  const discount =
    smBusinessInfo != null
      ? smBusinessInfo.smbizBusinessInvites.discountPercentage
      : 0;

  const customYearlyMemberships: Plan[] = [];
  memberships.map((m) => {
    if (
      (m.type == 'custom_year' || m.type == 'year') &&
      m.showOnEnrollment == true &&
      m.monthlyId
    )
      customYearlyMemberships[m.monthlyId] = m;
  });
  const customMonthlyMemberships = memberships.filter(
    (m) =>
      (m.type == 'month' || m.type == 'custom') &&
      (m.showOnEnrollment == true ||
        (m.showOnEnrollment == false &&
          Number(m.price) == 0 &&
          customYearlyMemberships[m.id] != undefined &&
          customYearlyMemberships[m.id].showOnEnrollment == true) ||
        (m.showOnEnrollment == false &&
          customYearlyMemberships[m.id] != undefined &&
          customYearlyMemberships[m.id].showOnEnrollment == true)),
  );

  customMonthlyMemberships.sort((a, b) => {
    const nameA = a.sortOrder;
    const nameB = b.sortOrder;
    if (nameA < nameB) {
      return -1;
    } else if (nameA > nameB) {
      return 1;
    }
    return 0;
  });

  return (
    <Wrapper>
      {customMonthlyMemberships.map((membership) => {
        let discounted_price =
          Math.round(Number(membership.price) * (100 - discount)) / 100;
        if (membership.showOnEnrollment == false) discounted_price = 0;
        const yearly_discounted =
          customYearlyMemberships[membership.id] == undefined
            ? 0
            : Math.round(
                Number(customYearlyMemberships[membership.id].price) *
                  (100 - discount),
              ) / 100;
        return (
          <div key={membership.id} className="plan_box-wrapper">
            <div className="plan_box">
              <div className="container">
                <div className="plan_description">
                  <div className="plan_header">
                    {isSpanish && membership.spanishName
                      ? membership.spanishName
                      : membership.name}
                    &ensp;
                    {membership.maxAge != null && membership.minAge == null && (
                      <span className="age_limits">
                        {intl.formatMessage(
                          { id: 'label.ageAndUnder' },
                          { age: membership.maxAge },
                        )}
                      </span>
                    )}
                    {membership.minAge != null && membership.maxAge == null && (
                      <span className="age_limits">
                        {intl.formatMessage(
                          { id: 'label.ageAndOver' },
                          { age: membership.minAge },
                        )}
                      </span>
                    )}
                    {membership.maxAge != null && membership.minAge != null && (
                      <span className="age_limits">
                        {intl.formatMessage(
                          { id: 'label.agesLimits' },
                          { max: membership.maxAge, min: membership.minAge },
                        )}
                      </span>
                    )}
                  </div>
                  <ul>
                    {(
                      (isSpanish && membership?.spanishDescription
                        ? membership?.spanishDescription
                        : membership?.description) ?? ''
                    )
                      .split(/(\r\n|\n|\r)/gm)
                      .map((i) => i.replace(/(\r\n|\n|\r|- )/gm, ''))
                      .filter((i) => i)
                      .map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                  </ul>
                  {membership.saveAmount && (
                    <StyledBoxSaveAmount>
                      save ${membership.saveAmount}
                    </StyledBoxSaveAmount>
                  )}
                </div>
                <div className="plan_prices">
                  {discounted_price != 0 && (
                    <span>
                      <b>
                        $
                        {formatNumberAsCurrency(
                          discounted_price,
                          2,
                          false,
                          true,
                        )}
                        /{intl.formatMessage({ id: 'label.mo' })}
                        {membership.activationFee ? '*' : ''}
                      </b>
                    </span>
                  )}
                  {customYearlyMemberships[membership.id] != undefined && (
                    <span>
                      {' '}
                      {discounted_price != 0 &&
                        intl
                          .formatMessage({ id: 'label.or' })
                          .toLowerCase()}{' '}
                      <b>
                        $
                        {formatNumberAsCurrency(
                          yearly_discounted,
                          2,
                          false,
                          true,
                        )}
                        /{intl.formatMessage({ id: 'label.yr' })}
                      </b>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </Wrapper>
  );
};

export default ClassicPlans;
